import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInAnonymously,
  signInWithPopup,
  signInWithRedirect,
  signOut
} from 'firebase/auth';
import { doc, getDoc, getFirestore, setDoc, deleteDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { deleteToken, getMessaging, getToken, isSupported as isMessagingSupported, onMessage } from 'firebase/messaging';


const AUTH_DOMAINS = ['wheniseeus.com', 'wheniseeus.web.app', 'wheniseeus.firebaseapp.com'];

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyC4_BHV94t5q_ZsIkZWt7ZL7gHx2Jhdn5w",
  // The Google sign-in handler (/__/auth/handler) is served by Firebase
  // Hosting on every domain the app lives on, so it runs same-origin as the
  // page - required for signInWithRedirect on mobile Safari/Chrome, whose
  // storage partitioning breaks a cross-domain handler. All three are
  // authorized redirect URIs on the OAuth client. Anything else (localhost
  // dev) falls back to the default domain.
  authDomain: typeof window !== 'undefined' && AUTH_DOMAINS.includes(window.location.hostname)
    ? window.location.hostname
    : "wheniseeus.firebaseapp.com",
  projectId: "wheniseeus",
  storageBucket: "wheniseeus.firebasestorage.app",
  messagingSenderId: "980198671592",
  appId: "1:980198671592:web:92c1f3fe4c77fdb617755d",
  measurementId: "G-6HLX5PB5YT"
};

// Wheniseeus Code AI on the VS Code Marketplace (vscode-extension/).
export const VSCODE_EXTENSION_URL = 'https://marketplace.visualstudio.com/items?itemName=dapurtechnology.wheniseeus';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

// Analytics needs its own async init (isSupported() checks things like
// cookies/IndexedDB availability that don't exist during SSR or in some
// embedded webviews) and, once ready, is cached here so trackEvent can fire
// synchronously from anywhere in the app without every caller having to
// await initAnalytics() first.
let analyticsInstance = null;

async function initAnalytics() {
  const supported = await isSupported();
  if (!supported) {
    return null;
  }
  analyticsInstance = getAnalytics(app);
  return analyticsInstance;
}

// Fire-and-forget event logging - analytics should never be a reason a real
// user action fails, so this silently no-ops until initAnalytics() has
// resolved (or forever, on unsupported environments).
function trackEvent(eventName, params) {
  if (!analyticsInstance) {
    return;
  }
  logEvent(analyticsInstance, eventName, params);
}

async function ensureAnonymousAuth() {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  const credential = await signInAnonymously(auth);
  return credential.user;
}

function watchAuthState(callback) {
  return onAuthStateChanged(auth, callback);
}

// Phones and tablets: popups are often blocked or open as a separate tab
// that never returns, so sign-in goes through a full-page redirect there.
function prefersRedirectSignIn() {
  if (typeof window === 'undefined') return false;
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile|Silk|Opera Mini/i.test(navigator.userAgent || '');
  const coarse = window.matchMedia?.('(pointer: coarse)').matches && window.innerWidth < 1024;
  return mobileUa || !!coarse;
}

/**
 * Google sign-in for the web app: a popup on computers, a redirect on
 * mobile (and as a fallback when a desktop browser blocks the popup).
 * Uses no Firebase Dynamic Links - only the standard web OAuth flow, which
 * the Dynamic Links shutdown (Aug 2025) doesn't affect. Resolves with
 * { user, isNewUser } after a popup, or null when the page is about to
 * redirect (the result then arrives via completeGoogleRedirect).
 */
async function signInWithGoogle() {
  googleProvider.setCustomParameters({ prompt: 'select_account' });
  if (prefersRedirectSignIn()) {
    await signInWithRedirect(auth, googleProvider);
    return null;
  }
  try {
    const credential = await signInWithPopup(auth, googleProvider);
    return { user: credential.user, isNewUser: !!getAdditionalUserInfo(credential)?.isNewUser };
  } catch (error) {
    if (error?.code === 'auth/popup-blocked' || error?.code === 'auth/operation-not-supported-in-this-environment') {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw error;
  }
}

/** Finishes a redirect sign-in on page load; null if there wasn't one. */
async function completeGoogleRedirect() {
  const credential = await getRedirectResult(auth);
  if (!credential) return null;
  return { user: credential.user, isNewUser: !!getAdditionalUserInfo(credential)?.isNewUser };
}

async function signInWithEmail(email, password) {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

async function registerWithEmail(email, password) {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  return credential.user;
}

async function signOutUser() {
  await signOut(auth);
}

// languageCode becomes the link's lang=, so Firebase's "choose a new
// password" page opens in the app's language instead of English.
async function sendPasswordReset(email, language) {
  if (language) auth.languageCode = language;
  await sendPasswordResetEmail(auth, email);
}

// Push notifications (workspace chat only - see functions/main.py's
// onWorkspaceMessageCreated, which is the only thing that actually sends
// one). Cached like analyticsInstance above: getMessaging() throws outside
// a supported context (no service worker support, insecure origin, etc.),
// so every caller goes through this rather than repeating the isSupported()
// dance.
let messagingInstance = null;
let messagingSupportChecked = false;

async function getMessagingIfSupported() {
  if (!messagingSupportChecked) {
    messagingSupportChecked = true;
    if (await isMessagingSupported().catch(() => false)) {
      messagingInstance = getMessaging(app);
    }
  }
  return messagingInstance;
}

// Registers this browser for push notifications and stores the resulting
// FCM token under the signed-in user's own subcollection (see
// firestore.rules - the user owns writes there, unlike the rest of their
// /users/{uid} doc). Call from a user gesture (a "enable notifications"
// button click) since requestPermission() requires one in most browsers.
// Returns the token on success, or null if unsupported/denied.
async function enableWorkspacePushNotifications(uid) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(messaging, {
    vapidKey: process.env.REACT_APP_FCM_VAPID_KEY,
    serviceWorkerRegistration: registration
  });
  if (!token) return null;

  await setDoc(doc(db, 'users', uid, 'fcmTokens', token), {
    createdAt: Date.now(),
    userAgent: navigator.userAgent
  });
  return token;
}

// Whether *this browser* already has a live, registered subscription -
// the durable source of truth (Notification.permission + a matching
// Firestore token doc), not any in-memory component state, so a toggle
// button can show the right state right after mount/reload instead of
// always starting "off" regardless of reality. Never prompts: it only
// asks FCM for the token once permission is already 'granted' (getToken
// doesn't prompt in that case, it just returns the existing token).
async function isWorkspacePushSubscribed(uid) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

  const messaging = await getMessagingIfSupported();
  if (!messaging) return false;

  const token = await getToken(messaging, { vapidKey: process.env.REACT_APP_FCM_VAPID_KEY }).catch(() => null);
  if (!token) return false;

  const snap = await getDoc(doc(db, 'users', uid, 'fcmTokens', token)).catch(() => null);
  return !!snap?.exists();
}

// Unregisters this browser's token both from FCM and from Firestore, so
// the send function (functions/main.py) stops targeting it.
async function disableWorkspacePushNotifications(uid) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return;

  const token = await getToken(messaging, { vapidKey: process.env.REACT_APP_FCM_VAPID_KEY }).catch(() => null);
  if (token) {
    await deleteDoc(doc(db, 'users', uid, 'fcmTokens', token)).catch(() => {});
    await deleteToken(messaging).catch(() => {});
  }
}

// Fires for a push that arrives while this tab is focused - the service
// worker's own background handler (public/firebase-messaging-sw.js) never
// sees those, so foreground display is this app's own responsibility.
async function watchForegroundPushNotifications(callback) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

export {
  app,
  auth,
  db,
  storage,
  disableWorkspacePushNotifications,
  enableWorkspacePushNotifications,
  ensureAnonymousAuth,
  initAnalytics,
  isWorkspacePushSubscribed,
  registerWithEmail,
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
  completeGoogleRedirect,
  signOutUser,
  trackEvent,
  watchAuthState,
  watchForegroundPushNotifications
};